import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../store/authStore.js'
import { Plus, Lock, Unlock, Trash2, Download, Printer, Search, X, Shield, ShieldOff, Ban, CheckCircle2, ChevronDown, Settings, Link as LinkIcon, Copy, RefreshCcw, Check } from 'lucide-react'

export default function AdminZugaenge() {
  const me = useAuth(s => s.user)
  const [users, setUsers] = useState(null)
  const [surveys, setSurveys] = useState([])
  const [filter, setFilter] = useState('')
  const [editing, setEditing] = useState(null)  // user object | 'new' | null
  const [hoveredUserId, setHoveredUserId] = useState(null)

  const load = async () => {
    const [u, s] = await Promise.all([api.listUsers(), api.listSurveys()])
    setUsers(u); setSurveys(s)
    return u
  }
  useEffect(() => { load() }, [])

  // refresh users in background and update the editing user reference if it's open
  const refreshAndSync = async () => {
    const u = await load()
    if (editing && editing !== 'new') {
      const fresh = u.find(x => x.id === editing.id)
      if (fresh) setEditing(fresh)
    }
  }

  const filtered = useMemo(() => {
    if (!users) return []
    const f = filter.toLowerCase()
    if (!f) return users
    return users.filter(u =>
      `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(f)
    )
  }, [users, filter])

  const exportCsv = () => {
    const rows = [['ID', 'Vorname', 'Nachname', 'E-Mail', 'Rolle', 'Status', 'Zugewiesen', 'Durchgeführt']]
    for (const u of filtered) {
      const assigned = u.accesses.map(a => `${a.surveyName} (${a.assignedAt?.slice(0, 10)})`).join('; ')
      const done = u.accesses.filter(a => a.completedAt).map(a => `${a.surveyName} (${a.completedAt?.slice(0, 10)})`).join('; ')
      rows.push([u.id, u.firstName, u.lastName, u.email, u.role, u.status, assigned, done])
    }
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'zugaenge.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  if (!users) return <div className="text-ink-soft">Lade…</div>

  return (
    <div className="space-y-6">
      <div className="max-w-[760px] mb-12">
        <p className="text-xs uppercase tracking-[0.14em] font-semibold text-teal mb-3.5">Verwaltung</p>
        <h1 className="text-[44px] leading-[1.05] tracking-[-0.025em] font-bold">Zugänge verwalten</h1>
        <p className="mt-3.5 text-base text-ink-soft leading-[1.55]">Personen anlegen, Umfragen freigeben und den Status nachverfolgen.</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-3">
        <div className="relative w-full sm:flex-1 sm:max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
          <input
            className="input pl-9" placeholder="Person suchen…"
            value={filter} onChange={e => setFilter(e.target.value)}
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <button onClick={() => setEditing('new')} className="btn-primary order-first sm:order-last w-full sm:w-auto"><Plus size={16} /> Zugang anlegen</button>
          <div className="flex items-center justify-end gap-4">
            <button onClick={exportCsv} className="text-sm text-ink-soft hover:text-teal-700 inline-flex items-center gap-1 transition">
              <Download size={14} /> CSV exportieren
            </button>
            <button onClick={() => window.print()} className="text-sm text-ink-soft hover:text-teal-700 inline-flex items-center gap-1 transition">
              <Printer size={14} /> Drucken
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-teal-50/60 text-ink-soft">
            <tr>
              <Th className="min-w-[100px]">E-Mail</Th><Th>Vorname</Th><Th>Nachname</Th><Th>Status</Th>
              <Th>Umfrage</Th>
              <Th>Zugewiesen</Th>
              <Th>Durchgeführt</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.flatMap(u => {
              // Bei 0 Umfragen: trotzdem eine Zeile mit „—" in den 3 Umfrage-Spalten
              const rows = u.accesses.length ? u.accesses : [null]
              return rows.map((a, idx) => {
                const isFirst = idx === 0
                const rowSpan = rows.length
                return (
                  <tr
                    key={`${u.id}-${a?.id || 'empty'}`}
                    onClick={() => setEditing(u)}
                    onMouseEnter={() => setHoveredUserId(u.id)}
                    onMouseLeave={() => setHoveredUserId(null)}
                    className={`align-top cursor-pointer transition
                      ${hoveredUserId === u.id ? 'bg-teal-50/60' : ''}
                      ${isFirst ? 'border-t-2 border-teal-200' : 'border-t border-teal-100/40'}`}
                  >
                    {isFirst && (
                      <>
                        <Td rowSpan={rowSpan} className="text-ink-soft text-sm break-all min-w-[100px]">{u.email}</Td>
                        <Td rowSpan={rowSpan}>{u.firstName}</Td>
                        <Td rowSpan={rowSpan}>{u.lastName}</Td>
                        <Td rowSpan={rowSpan}>
                          <div className="flex flex-wrap gap-1">
                            {u.status === 'active'
                              ? <span className="badge bg-emerald-100 text-emerald-700">Aktiv</span>
                              : <span className="badge bg-amber-100 text-amber-700">Gesperrt</span>}
                            {u.role === 'admin' && (
                              <span className="badge bg-teal-100 text-teal-800"><Shield size={11} /> Admin</span>
                            )}
                          </div>
                        </Td>
                      </>
                    )}
                    <Td>
                      {a ? (
                        <span className="text-sm">{a.surveyName}</span>
                      ) : (
                        <span className="text-xs text-ink-mute italic">Keine Umfrage zugewiesen</span>
                      )}
                    </Td>
                    <Td className="text-sm whitespace-nowrap">
                      {a?.assignedAt ? a.assignedAt.slice(0, 10) : <span className="text-ink-mute">—</span>}
                    </Td>
                    <Td className="text-sm whitespace-nowrap">
                      {a?.completedAt
                        ? a.completedAt.slice(0, 10)
                        : <span className="text-ink-mute">—</span>}
                    </Td>
                  </tr>
                )
              })
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center text-ink-soft">Keine Personen gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ZugangModal
          mode={editing === 'new' ? 'new' : 'edit'}
          user={editing === 'new' ? null : editing}
          surveys={surveys}
          me={me}
          onClose={() => setEditing(null)}
          onChanged={refreshAndSync}
          onSaved={() => { setEditing(null); load() }}
          onCreated={async (newUserId) => {
            const u = await load()
            const fresh = u.find(x => x.id === newUserId)
            if (fresh) setEditing(fresh)  // wechselt in edit-mode mit Invite-Link sichtbar
          }}
        />
      )}
    </div>
  )
}

function Th({ children, className = '' }) {
  return <th className={`px-4 py-3 text-left font-medium ${className}`}>{children}</th>
}
function Td({ children, className = '', ...props }) {
  return <td className={`px-4 py-3 ${className}`} {...props}>{children}</td>
}

function ZugangModal({ mode, user, surveys, me, onClose, onChanged, onSaved, onCreated }) {
  const [form, setForm] = useState({
    salutation: user?.salutation || 'Herr',
    title: user?.title || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    language: user?.language || 'de',
    email: user?.email || '',
    inviteByEmail: true,
  })
  // Im New-Modus existiert noch kein User → Auswahl wird gesammelt und nach createUser angelegt.
  const [pendingSurveys, setPendingSurveys] = useState(() => new Set())
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)

  const accessBySurvey = useMemo(() => {
    const m = new Map()
    for (const a of (user?.accesses || [])) m.set(a.surveyId, a)
    return m
  }, [user])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const save = async () => {
    setSaving(true); setError(null)
    try {
      let saved
      if (mode === 'new') {
        saved = await api.createUser(form)
        if (saved?.id && pendingSurveys.size > 0) {
          for (const sid of pendingSurveys) {
            await api.createAccess({ userId: saved.id, surveyId: sid })
          }
        }
      } else {
        saved = await api.updateUser(user.id, form)
      }
      if (mode === 'new' && saved?.id) {
        // Modal offen lassen und in Edit-Modus wechseln, damit Admin den Invite-Link gleich kopieren kann
        await onCreated?.(saved.id)
      } else {
        onSaved()
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const toggleSurveyAssignment = async (surveyId) => {
    if (mode === 'new') {
      setPendingSurveys(prev => {
        const next = new Set(prev)
        if (next.has(surveyId)) next.delete(surveyId); else next.add(surveyId)
        return next
      })
      return
    }
    const existing = accessBySurvey.get(surveyId)
    setError(null)
    setBusy(true)
    try {
      if (existing) {
        if (!confirm('Zugang wirklich entziehen? Bereits gegebene Antworten gehen verloren.')) return
        await api.deleteAccess(existing.id)
      } else {
        await api.createAccess({ userId: user.id, surveyId })
      }
      await onChanged?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const toggleAccessLock = async (access) => {
    setError(null)
    setBusy(true)
    try {
      if (access.locked) await api.unlockAccess(access.id)
      else await api.lockAccess(access.id)
      await onChanged?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value })

  const isSelf = mode === 'edit' && user?.id === me?.id

  const toggleStatus = async () => {
    if (mode !== 'edit') return
    if (isSelf && user.status === 'active') {
      setError('Sie können sich nicht selbst sperren.')
      return
    }
    setBusy(true); setError(null)
    try {
      if (user.status === 'active') await api.blockUser(user.id)
      else await api.unblockUser(user.id)
      await onChanged?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const toggleRole = async () => {
    if (mode !== 'edit') return
    const newRole = user.role === 'admin' ? 'employee' : 'admin'
    if (isSelf && newRole !== 'admin') {
      setError('Sie können sich nicht selbst die Admin-Rechte entziehen.')
      return
    }
    const verb = newRole === 'admin' ? 'zum Administrator machen' : 'die Admin-Rechte entziehen'
    if (!confirm(`${user.firstName} ${user.lastName} ${verb}?`)) return
    setBusy(true); setError(null)
    try {
      await api.setUserRole(user.id, newRole)
      await onChanged?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const deleteSelf = async () => {
    if (mode !== 'edit') return
    if (isSelf) { setError('Sie können sich nicht selbst löschen.'); return }
    if (!confirm(`${user.firstName} ${user.lastName} wirklich vollständig löschen? Alle zugewiesenen Umfragen und Antworten werden mitgelöscht. Dies kann nicht rückgängig gemacht werden.`)) return
    setBusy(true); setError(null)
    try {
      await api.deleteUser(user.id)
      onSaved()
    } catch (e) {
      setError(e.message); setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-ink/70 backdrop-blur-sm z-50 overflow-y-auto !mt-0" onClick={onClose}>
      <div className="min-h-full flex items-start justify-center p-4 sm:py-12">
      <div className="bg-white rounded-2xl shadow-card w-full max-w-2xl xl:max-w-7xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start sm:items-center justify-between gap-3 p-4 sm:p-5 bg-teal text-white rounded-t-2xl">
          <h2 className="font-bold text-base sm:text-lg flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <span className="break-words">{mode === 'new' ? 'Zugang anlegen' : 'Zugang bearbeiten'}</span>
            {mode === 'edit' && user?.status === 'blocked' && (
              <span className="badge bg-amber-100 text-amber-800 shrink-0">Gesperrt</span>
            )}
            {mode === 'edit' && user?.role === 'admin' && (
              <span className="badge bg-white/20 text-white border border-white/30 shrink-0"><Shield size={11} /> Admin</span>
            )}
          </h2>
          <div className="flex items-center gap-1 shrink-0">
            {mode === 'edit' && (
              <ZugangActionsMenu
                user={user} isSelf={isSelf} busy={busy}
                onToggleStatus={toggleStatus} onToggleRole={toggleRole} onDelete={deleteSelf}
              />
            )}
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg" aria-label="Schließen"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6 space-y-6 min-w-0">
          <Section title="Profildaten">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <Field label="Anrede *">
                <select className="input" value={form.salutation} onChange={update('salutation')}>
                  <option>Herr</option><option>Frau</option><option>Divers</option>
                </select>
              </Field>
              <Field label="Titel"><input className="input" value={form.title} onChange={update('title')} /></Field>
              <Field label="Vorname *"><input className="input" value={form.firstName} onChange={update('firstName')} /></Field>
              <Field label="Nachname *"><input className="input" value={form.lastName} onChange={update('lastName')} /></Field>
              <Field label="Telefon"><input className="input" value={form.phone} onChange={update('phone')} /></Field>
              <Field label="Sprache">
                <select className="input" value={form.language} onChange={update('language')}>
                  <option value="de">Deutsch</option><option value="en">English</option>
                </select>
              </Field>
            </div>
          </Section>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-10 min-w-0">
          <Section title="Anmeldeinformationen">
            <Field label="E-Mail-Adresse (zugleich Login-Name)">
              <input className="input" type="email" value={form.email} onChange={update('email')} />
            </Field>
            {mode === 'edit' && (
              <div className="mt-4">
                <InviteLinkPanel user={user} />
              </div>
            )}
          </Section>

          <div className="xl:border-l xl:border-[#EAEBEF] xl:pl-10">
          <Section title="Umfragezugang">
            <div className="space-y-2">
              {surveys.map(s => {
                const access = accessBySurvey.get(s.id)
                const checked = mode === 'new' ? pendingSurveys.has(s.id) : !!access
                return (
                  <div
                    key={s.id}
                    className={`flex items-center gap-3 p-2 pr-3 rounded-xl border transition ${
                      checked ? 'bg-teal-50/60 border-teal-100' : 'bg-white border-teal-100/40 hover:border-teal-200'
                    }`}
                  >
                    <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy}
                        onChange={() => toggleSurveyAssignment(s.id)}
                        className="h-4 w-4 accent-teal-600 shrink-0"
                      />
                      <div className="flex-1 min-w-0 text-sm">
                        <div className="font-medium truncate">{s.name}</div>
                        {access && (
                          <div className="text-xs text-ink-mute">
                            Zugewiesen {access.assignedAt?.slice(0, 10)}
                            {access.completedAt && ` · Abgeschlossen ${access.completedAt.slice(0, 10)}`}
                          </div>
                        )}
                      </div>
                    </label>
                    {access && (
                      <button
                        type="button"
                        onClick={() => toggleAccessLock(access)}
                        disabled={busy}
                        className="p-2 rounded-lg bg-teal text-white hover:bg-teal-600 disabled:opacity-50 shrink-0"
                        title={access.locked ? 'Entsperren' : 'Sperren'}
                      >
                        {access.locked ? <Unlock size={14} /> : <Lock size={14} />}
                      </button>
                    )}
                  </div>
                )
              })}
              {surveys.length === 0 && (
                <div className="text-sm text-ink-mute italic">Keine Umfragen verfügbar.</div>
              )}
            </div>
          </Section>
          </div>
          </div>

          {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3">{error}</div>}
        </div>
        <div className="p-5 bg-teal-50/40 border-t border-teal-100 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="btn-secondary">Abbrechen</button>
          <button onClick={save} disabled={saving} className="btn-primary">{saving ? 'Speichert…' : 'Speichern'}</button>
        </div>
      </div>
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="font-semibold text-ink mb-3">{title}</h3>
      {children}
    </div>
  )
}
function Field({ label, children }) {
  return <div><label className="label">{label}</label>{children}</div>
}

function InviteLinkPanel({ user }) {
  const [token, setToken] = useState(user?.activeInviteToken || null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState(null)

  const url = token ? `${window.location.origin}/invite/${token}` : null
  const hasPassword = !!user?.hasPassword

  const generate = async () => {
    setBusy(true); setError(null)
    try {
      const res = await api.generateInvite(user.id)
      setToken(res.token)
      setCopied(false)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* ignore */ }
  }

  const purposeLabel = hasPassword ? 'Passwort-Reset-Link' : 'Einladungslink'
  const helpText = hasPassword
    ? 'Das Konto hat bereits ein Passwort. Mit einem Reset-Link kann der/die Nutzer:in ein neues Passwort vergeben (z.B. nach Vergessen).'
    : 'Das Konto hat noch kein Passwort. Mit dem Einladungslink kann der/die Nutzer:in das Konto aktivieren.'

  return (
    <div className="rounded-xl border border-teal-100 bg-teal-50/40 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <LinkIcon size={16} className="text-teal-700 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{purposeLabel}</div>
          <p className="text-xs text-ink-soft leading-snug mt-0.5">{helpText}</p>
        </div>
      </div>

      {url ? (
        <>
          <div className="flex items-stretch gap-2 min-w-0">
            <input
              readOnly value={url}
              onFocus={e => e.target.select()}
              className="input text-xs font-mono bg-white min-w-0 flex-1"
            />
            <button onClick={copy} className={`btn shrink-0 ${copied ? 'bg-emerald-500 text-white' : 'bg-teal text-white hover:bg-teal-600'} px-3`} title="In Zwischenablage kopieren">
              {copied ? <><Check size={14} /> Kopiert</> : <><Copy size={14} /> Kopieren</>}
            </button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-[11px] text-ink-mute">Link gilt 14 Tage und kann nur einmal verwendet werden.</p>
            <button onClick={generate} disabled={busy} className="text-xs text-teal hover:text-teal-700 inline-flex items-center gap-1 self-start sm:self-auto">
              <RefreshCcw size={12} /> Neu generieren
            </button>
          </div>
        </>
      ) : (
        <button onClick={generate} disabled={busy} className="btn-secondary text-sm py-1.5">
          <LinkIcon size={14} /> {busy ? 'Erstelle…' : `${purposeLabel} erstellen`}
        </button>
      )}

      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  )
}

function ZugangActionsMenu({ user, isSelf, busy, onToggleStatus, onToggleRole, onDelete }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('mousedown', onClick)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('mousedown', onClick)
    }
  }, [open])

  const wrap = (fn) => async () => { setOpen(false); await fn() }

  const isActive = user.status === 'active'
  const isAdmin = user.role === 'admin'
  const blockDisabled = isSelf && isActive
  const adminRemoveDisabled = isSelf && isAdmin
  const deleteDisabled = isSelf

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 disabled:opacity-50 text-sm font-medium"
      >
        <Settings size={15} /> Zugang
        <ChevronDown size={14} className={`transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-card border border-teal-100/60 py-2 text-ink z-10">
          <MenuItem
            icon={isActive ? <Ban size={15} /> : <CheckCircle2 size={15} />}
            label={isActive ? 'Sperren' : 'Entsperren'}
            onClick={wrap(onToggleStatus)}
            disabled={blockDisabled}
            hint={blockDisabled ? 'Sie können sich nicht selbst sperren.' : null}
          />
          <MenuItem
            icon={isAdmin ? <ShieldOff size={15} /> : <Shield size={15} />}
            label={isAdmin ? 'Admin-Rechte entfernen' : 'Zum Admin machen'}
            onClick={wrap(onToggleRole)}
            disabled={adminRemoveDisabled}
            hint={adminRemoveDisabled ? 'Sie können sich nicht selbst entfernen.' : null}
          />
          <div className="my-1 border-t border-teal-100" />
          <MenuItem
            icon={<Trash2 size={15} />}
            label="Zugang löschen"
            onClick={wrap(onDelete)}
            disabled={deleteDisabled}
            hint={deleteDisabled ? 'Sie können sich nicht selbst löschen.' : null}
            danger
          />
        </div>
      )}
    </div>
  )
}

function MenuItem({ icon, label, onClick, disabled, hint, danger }) {
  return (
    <div>
      <button
        role="menuitem"
        onClick={onClick}
        disabled={disabled}
        className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition
          ${disabled ? 'text-ink-mute cursor-not-allowed' : 'hover:bg-teal-50'}
          ${danger && !disabled ? 'text-rose-600 hover:bg-rose-50' : ''}`}
      >
        {icon} <span>{label}</span>
      </button>
      {hint && <div className="px-4 pb-1.5 text-[11px] text-ink-mute">{hint}</div>}
    </div>
  )
}
